import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useGlobalAlertStream } from './useGlobalAlertStream';
import type { AlertEvent } from './useMonitoringAlerts';
import { useAlertStore } from '../store/alertStore';
import { useZoneStore } from '../store/zoneStore';
import { alertService } from '../services/alertService';
import { puToVolts } from '../utils/electricalConstants';
import { formatElementInfo } from '../utils/elementTypeLabels';
import { fmt } from '../utils/numberFormat';

/**
 * Global alert hook — fires toast notifications for all alert types
 * across ALL zones and publishes events to the alert store.
 *
 * Uses the zone-independent /api/alerts/stream SSE endpoint so alerts
 * from other zones are never missed.
 *
 * Call once in App.tsx with no arguments.
 */
export function useGlobalAlerts(): void {
  const setLastAlert = useAlertStore(s => s.setLastAlert);
  const addAlert = useAlertStore(s => s.addAlert);
  const backfillHistory = useAlertStore(s => s.backfillHistory);
  const backfillDone = useRef(false);

  // Backfill alert history from REST on first mount
  useEffect(() => {
    if (backfillDone.current) return;
    backfillDone.current = true;

    alertService.getAlertHistory('24h', 50).then(entries => {
      backfillHistory(entries);
    }).catch(err => {
      console.error('[GlobalAlerts] Failed to backfill alert history:', err);
    });
  }, [backfillHistory]);

  const handleAlert = useCallback((alert: AlertEvent) => {
    // Resolve zone name from store
    const zones = useZoneStore.getState().zones;
    const zoneName = zones.find(z => z.id === alert.data.zoneId)?.name ?? alert.data.zoneId;

    // 1. Fire toast with zone prefix
    if (alert.type === 'trafo_alert') {
      const d = alert.data;
      const pct = fmt(d.loadingPercent, 1);
      if (d.newState === 'BLACK') {
        toast.error(`[${zoneName}] Trafo-Alarm: \u00dcberlast \u2014 ${pct}% Auslastung`);
      } else if (d.newState === 'RED') {
        toast.warning(`[${zoneName}] Trafo-Alarm: Gef\u00e4hrdet \u2014 ${pct}% Auslastung`);
      }
    } else if (alert.type === 'cable_alert') {
      const d = alert.data;
      const pct = fmt(d.loadingPercent, 1);
      if (d.newState === 'BLACK') {
        toast.error(`[${zoneName}] Kabel-Alarm: \u00dcberlast \u2014 ${pct}% Auslastung`);
      } else if (d.newState === 'RED') {
        toast.warning(`[${zoneName}] Kabel-Alarm: Gef\u00e4hrdet \u2014 ${pct}% Auslastung`);
      }
    } else {
      const d = alert.data;
      if (d.newState === 'RED' || d.newState === 'YELLOW') {
        const pu = fmt(d.voltagePu, 3);
        const volts = fmt(puToVolts(d.voltagePu), 1);
        const phaseLabel = d.phase ? ` ${d.phase}` : '';
        const kind = d.triggerType === 'UNDERVOLTAGE' ? 'Unterspannung' : '\u00dcberspannung';
        const elementInfo = formatElementInfo(d.elementType, d.elementName);
        const elementSuffix = elementInfo ? ` bei ${elementInfo}` : '';
        if (d.newState === 'RED') {
          toast.error(`[${zoneName}] Spannungsverletzung:${phaseLabel} ${kind}${elementSuffix} \u2014 ${pu} p.u. (${volts} V)`);
        } else {
          toast.warning(`[${zoneName}] Spannungswarnung:${phaseLabel} ${kind}${elementSuffix} \u2014 ${pu} p.u. (${volts} V)`);
        }
      }
    }

    // 2. Publish to alert store for page-specific consumers
    setLastAlert(alert);

    // 3. Add to cross-zone alert history (with dedup)
    addAlert(alert);
  }, [setLastAlert, addAlert]);

  useGlobalAlertStream(handleAlert);
}
